import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Clock, ShieldCheck, Flame, Home as HomeIcon, Utensils, Leaf, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { FoodCard } from '../components/FoodCard';
import { useCart } from '../context/CartContext';
import SEO from '../components/SEO';

const DEFAULT_CATEGORY_IMAGES = {
  breakfast: 'https://images.pexels.com/photos/376464/pexels-photo-376464.jpeg?w=300',
  biryani: 'https://images.pexels.com/photos/9738983/pexels-photo-9738983.jpeg?w=300',
  curries: 'https://images.pexels.com/photos/37295815/pexels-photo-37295815.jpeg?w=300',
  breads: 'https://images.pexels.com/photos/1117862/pexels-photo-1117862.jpeg?w=300',
  starters: 'https://images.pexels.com/photos/37153389/pexels-photo-37153389.jpeg?w=300',
  desserts: 'https://images.pexels.com/photos/9198596/pexels-photo-9198596.jpeg?w=300',
  beverages: 'https://images.unsplash.com/photo-1568909344668-6f14a07b56a0?w=300',
};
const FALLBACK_CATEGORY_IMAGE = 'https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?w=300';

const STATIC_CATEGORIES = [
  { name: 'Breakfast', img: 'https://images.pexels.com/photos/376464/pexels-photo-376464.jpeg?w=300' },
  { name: 'Biryani', img: 'https://images.pexels.com/photos/9738983/pexels-photo-9738983.jpeg?w=300' },
  { name: 'Curries', img: 'https://images.pexels.com/photos/37295815/pexels-photo-37295815.jpeg?w=300' },
  { name: 'Starters', img: 'https://images.pexels.com/photos/37153389/pexels-photo-37153389.jpeg?w=300' },
  { name: 'Desserts', img: 'https://images.pexels.com/photos/9198596/pexels-photo-9198596.jpeg?w=300' },
  { name: 'Beverages', img: 'https://images.unsplash.com/photo-1568909344668-6f14a07b56a0?w=300' },
];

const STORY_CARDS = [
  {
    icon: HomeIcon,
    title: 'Home Kitchen',
    text: 'Cooked in our home kitchen — never mass-produced, always fresh to order.',
    color: 'bg-maroon',
    gradient: 'from-maroon/10 to-saffron/10',
  },
  {
    icon: Utensils,
    title: 'Only Home Spices',
    text: 'The same jars your grandma uses. Whole spices and spices powders.',
    color: 'bg-saffron',
    gradient: 'from-saffron/10 to-golden/20',
  },
  {
    icon: Leaf,
    title: 'Zero Artificial Additives',
    text: 'No preservatives, no colours, no MSG. Just ingredients your family would recognise.',
    color: 'bg-fresh',
    gradient: 'from-fresh/10 to-emerald-100',
  },
];

export default function Home() {
  const { systemConfig } = useCart();
  const [featured, setFeatured] = useState([]);
  const [todaysSpecial, setTodaysSpecial] = useState([]);
  const [categoriesList, setCategoriesList] = useState([]);
  const [activeStory, setActiveStory] = useState(0);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 500 : false);

  const prevStory = () => {
    setActiveStory((prev) => (prev - 1 + 3) % 3);
  };

  const nextStory = () => {
    setActiveStory((prev) => (prev + 1) % 3);
  };

  useEffect(() => {
    api.get('/categories').then((res) => {
      if (res.data && Array.isArray(res.data)) {
        setCategoriesList(res.data.filter((c) => c.is_active !== false));
      }
    }).catch(() => {});

    api.get('/menu').then((r) => {
      setFeatured(r.data.filter((i) => i.is_featured).slice(0, 6));
      setTodaysSpecial(r.data.filter((i) => i.is_todays_special));
    });

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 500);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getBannerMessage = () => {
    if (!systemConfig) return '';
    if (systemConfig.is_online === false) {
      if (!systemConfig.allow_preorders) {
        return 'Kitchen is offline. Please check after some time.';
      } else {
        return 'Kitchen is offline but you can preorder food.';
      }
    }
    const now = new Date();
    const utc = now.getTime() + now.getTimezoneOffset() * 60000;
    const istDate = new Date(utc + (5.5 * 60 * 60000));
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDay = dayNames[istDate.getDay()];
    const isDayOperational = systemConfig.operating_hours?.[currentDay]?.is_open;
    if (!isDayOperational) {
      const days_ordered = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      const operationalDaysList = days_ordered
        .filter(day => systemConfig.operating_hours?.[day]?.is_open)
        .map(day => day.charAt(0).toUpperCase() + day.slice(1));
      return `We only serve on ${operationalDaysList.join(', ')}.`;
    }
    if (systemConfig.allow_preorders) {
      return `We are currently Closed. We are accepting pre-orders only. Next open: ${systemConfig.next_opening || 'soon'}.`;
    } else {
      return `Kitchen is Offline. Order placement is disabled. We will open again ${systemConfig.next_opening || 'soon'}.`;
    }
  };

  return (
    <div className="pb-24 md:pb-8">
      <SEO 
        title="Pushpanjali Noida | Homemade Food, Biryani & Tiffin Delivery"
        description="Order fresh homemade Indian food, slow-cooked chicken butter masala, aromatic biryani, and healthy meals in Noida. Authentic tiffin delivery in 30 minutes!"
        keywords="home made food Noida, fresh food, cloud kitchen, food in noida, home food in noida, ghar ka khana, biryani delivery noida, chicken curry, butter chicken, tiffin service noida, meals for working professionals Noida, desi food, order food sector 76 noida"
      />
      {!systemConfig?.is_currently_open && (
        <div className={`py-3 px-4 text-center text-xs md:text-sm font-semibold flex items-center justify-center gap-2 border-b leading-snug ${
          systemConfig?.allow_preorders 
            ? 'bg-amber-50 text-amber-800 border-amber-200' 
            : 'bg-red-50 text-red-900 border-red-200'
        }`}>
          <Clock className="w-4 h-4 shrink-0" />
          <span>{getBannerMessage()}</span>
        </div>
      )}
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-hero text-white grain">
        <div className="absolute inset-0 opacity-20">
          <img src="https://images.pexels.com/photos/9792458/pexels-photo-9792458.jpeg" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-20 grid md:grid-cols-2 gap-8 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur px-3 py-1.5 rounded-full mb-4 border border-white/20">
              <Sparkles className="w-4 h-4 text-golden" />
              {/* <img src="/pushpanjaliLogoMain.png" alt="Logo" className="w-full h-full object-contain" /> */}
              <span className="text-xs font-body font-medium">Free delivery on orders above ₹199</span>
            </div>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-tight mb-4">
              Authentic <span className="text-golden">Biryani</span>,<br />
              Fresh Curries &<br />
              <span className="text-golden">Homemade</span> Goodness
            </h1>
            <p className="font-body text-white/85 text-base mb-8 max-w-md">
              A cloud kitchen serving freshly prepared homemade meals, wholesome breakfasts, flavorful curries, aromatic biryanis, and comforting daily favorites—crafted with quality ingredients and delivered fresh to your doorstep.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/menu" data-testid="hero-order-now-btn" className="inline-flex items-center gap-2 bg-white text-maroon font-heading font-bold px-6 py-3 rounded-2xl shadow-warm hover:scale-105 transition-transform">
                Order Now <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/menu" data-testid="hero-explore-menu-btn" className="inline-flex items-center gap-2 border-2 border-white text-white font-heading font-semibold px-6 py-3 rounded-2xl hover:bg-white hover:text-maroon transition-colors">
                Explore Menu
              </Link>
            </div>

            <div className="grid grid-cols-3 gap-4 mt-10 max-w-md">
              {[
                { icon: Clock, label: '30 min*', sub: 'Delivery' },
                { icon: Flame, label: '100+', sub: 'Recipes' },
                { icon: ShieldCheck, label: '4.8★', sub: 'Rated' },
              ].map((s, i) => (
                <div key={i} className="text-center">
                  <s.icon className="w-5 h-5 text-golden mx-auto mb-1" />
                  <div className="font-heading font-bold text-lg">{s.label}</div>
                  <div className="text-white/70 text-xs font-body">{s.sub}</div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
            className="hidden md:block relative"
          >
            <div className="relative w-full aspect-square max-w-md ml-auto">
              <div className="absolute inset-0 rounded-full bg-gradient-primary opacity-40 blur-3xl animate-pulse-soft" />
              <img
                src="https://images.pexels.com/photos/9738983/pexels-photo-9738983.jpeg"
                alt="Biryani"
                className="relative rounded-full w-full h-full object-cover shadow-2xl border-8 border-white/20 animate-float"
              />
              <motion.div
                initial={{ scale: 0, rotate: -30 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.5, type: 'spring' }}
                className="absolute -top-4 -left-4 bg-gradient-offer px-4 py-3 rounded-2xl shadow-warm"
              >
                <div className="text-xs font-body text-textprimary/70">Save up to</div>
                <div className="font-heading font-extrabold text-2xl text-maroon">40% OFF</div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Offer Banner strip */}
      <div className="bg-maroon text-white py-2 overflow-hidden w-full flex">
        <div className="flex gap-8 whitespace-nowrap text-sm font-body animate-marquee">
          {Array(8).fill(0).map((_, i) => (
            <div key={i} className="flex gap-8 shrink-0">
              <span>✨ FLAT 20% OFF on Biryanis</span>
              <span>🚀 Free delivery above ₹199</span>
              <span>🎁 Buy 1 Paratha Get 1 Free</span>
              <span>🔥 Bestseller: Chicken Biryani</span>
            </div>
          ))}
        </div>
      </div>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-10">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-textprimary">What's on your mind?</h2>
            <p className="text-textsecondary font-body text-sm">Explore our authentic Indian categories</p>
          </div>
          <Link to="/menu" className="text-maroon text-sm font-heading font-semibold hidden md:inline">View all →</Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 md:gap-4">
          {(categoriesList.length > 0 ? categoriesList : STATIC_CATEGORIES).map((c, i) => {
            const keyName = c.id || c.name || `cat-${i}`;
            const catName = c.name || c.id;
            const imgUrl = c.img || c.image || DEFAULT_CATEGORY_IMAGES[c.id?.toLowerCase()] || DEFAULT_CATEGORY_IMAGES[c.name?.toLowerCase()] || FALLBACK_CATEGORY_IMAGE;
            return (
              <motion.div
                key={keyName}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <Link
                  to={`/menu?category=${encodeURIComponent(catName)}`}
                  data-testid={`category-${catName.toLowerCase()}`}
                  className="block group"
                >
                  <div className="aspect-square rounded-2xl overflow-hidden shadow-soft group-hover:shadow-warm group-hover:scale-105 transition-transform relative">
                    <img src={imgUrl} alt={catName} className="w-full h-full object-cover" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  </div>
                  <div className="mt-2 text-center font-heading font-semibold text-textprimary text-xs md:text-sm">{catName}</div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Home-Kitchen Story */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-fresh/10 px-3 py-1 rounded-full mb-3">
            <HomeIcon className="w-4 h-4 text-fresh" />
            <span className="text-xs font-heading font-bold text-fresh uppercase tracking-wider">Cloud Kitchen</span>
          </div>
          <h2 className="font-heading font-extrabold text-2xl md:text-3xl text-textprimary">Home-Cooked, Hygienically Prepared</h2>
          <p className="text-textsecondary font-body text-sm mt-2 max-w-xl mx-auto">
            No dine-in shortcuts. Every dish leaves our kitchen the same way it would leave a mother's — with care, real ingredients and zero compromise.
          </p>
        </div>

        {isMobile ? (
          <div className="relative max-w-2xl mx-auto px-4 md:px-0">
            <div className="overflow-hidden relative min-h-[220px] rounded-3xl border border-borderbeige shadow-card hover:shadow-warm bg-white transition-all">
              <AnimatePresence mode="wait">
                {STORY_CARDS.map((card, i) => i === activeStory ? (
                  <motion.div
                    key={card.title}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.3 }}
                    data-testid={`homekitchen-card-${i}`}
                    className={`bg-gradient-to-br ${card.gradient} p-8 min-h-[220px] flex flex-col md:flex-row items-center gap-6`}
                  >
                    <div className={`w-16 h-16 shrink-0 rounded-2xl ${card.color} flex items-center justify-center mb-4 shadow-warm`}>
                      <card.icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1 text-center md:text-left">
                      <h3 className="font-heading font-extrabold text-xl text-textprimary mb-2">{card.title}</h3>
                      <p className="text-sm text-textsecondary font-body leading-relaxed">{card.text}</p>
                    </div>
                  </motion.div>
                ) : null)}
              </AnimatePresence>

              {/* Navigation Arrows */}
              <button
                onClick={prevStory}
                className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-textprimary hover:text-maroon p-2 rounded-full shadow-md transition-colors active:scale-95 duration-200 animate-fade-in"
                aria-label="Previous story"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={nextStory}
                className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-textprimary hover:text-maroon p-2 rounded-full shadow-md transition-colors active:scale-95 duration-200 animate-fade-in"
                aria-label="Next story"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Dots Indicator */}
            <div className="flex justify-center gap-2 mt-4">
              {STORY_CARDS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveStory(i)}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${activeStory === i ? 'bg-maroon w-6' : 'bg-borderbeige'}`}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {STORY_CARDS.map((card, i) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                data-testid={`homekitchen-card-${i}`}
                className={`bg-gradient-to-br ${card.gradient} p-6 rounded-3xl border border-borderbeige shadow-card hover:shadow-warm transition-shadow`}
              >
                <div className={`w-12 h-12 rounded-2xl ${card.color} flex items-center justify-center mb-4 shadow-warm`}>
                  <card.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-heading font-bold text-lg text-textprimary mb-2">{card.title}</h3>
                <p className="text-sm text-textsecondary font-body leading-relaxed">{card.text}</p>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* Today's Special */}
      {(systemConfig?.enable_todays_special ?? true) && todaysSpecial.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-8" data-testid="todays-special-section">
          <div className="bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-amber-500/10 rounded-3xl p-6 md:p-8 border border-amber-200 shadow-soft">
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-saffron text-white px-3.5 py-1 rounded-full mb-2 shadow-sm">
                  <Sparkles className="w-4 h-4 text-golden animate-pulse" />
                  <span className="text-xs font-heading font-bold uppercase tracking-wider">Chef's Selection</span>
                </div>
                <h2 className="font-heading font-extrabold text-2xl md:text-3xl text-textprimary flex items-center gap-2">
                  {systemConfig?.todays_special_title || "Today's Special"}
                  <span className="text-amber-500">⭐</span>
                </h2>
                <p className="text-textsecondary font-body text-sm mt-1">Handpicked daily specials crafted fresh by our chef</p>
              </div>
              <Link to="/menu" data-testid="todays-special-view-all" className="text-maroon text-sm font-heading font-semibold hidden md:inline">Explore Menu →</Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {todaysSpecial.map((item) => (
                <FoodCard key={item.id} item={item} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="inline-flex items-center gap-2 bg-gradient-offer px-3 py-1 rounded-full mb-2">
              <Flame className="w-4 h-4 text-maroon" />
              <span className="text-xs font-heading font-bold text-maroon uppercase tracking-wider">Bestsellers</span>
            </div>
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-textprimary">Crowd Favorites</h2>
          </div>
          <Link to="/menu" data-testid="featured-view-all" className="text-maroon text-sm font-heading font-semibold">View all →</Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {featured.map((item) => (
            <FoodCard key={item.id} item={item} />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="relative rounded-3xl bg-gradient-primary p-8 md:p-12 overflow-hidden shadow-warm grain">
          <div className="relative z-10 max-w-xl">
            <h3 className="font-heading font-extrabold text-3xl md:text-4xl text-white mb-3">Hungry? We got you.</h3>
            <p className="text-white/90 font-body mb-6">Browse our full menu of biryanis, curries, tandoori grills, and desserts.</p>
            <Link to="/menu" data-testid="cta-order-now" className="inline-flex items-center gap-2 bg-white text-saffron px-6 py-3 rounded-2xl font-heading font-bold shadow-soft hover:scale-105 transition-transform">
              Start Ordering <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
